import { useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartDownloadButton from '../../../../../common/src/components/charts/ChartDownloadButton';

import {
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Tabs,
  Tab,
} from '@nextui-org/react';
import { useState, useMemo, useEffect } from 'react';
import { GroupedSettlement } from '../../../services/comparative-clientes/comparative-clients.utility';
import { formatMoneyUSD } from '../../../utils/functions';
import { sortWeekKeys } from 'common/src/utils/functions';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { QuartileDistribution } from '../../../services/comparative-industry/comparative-industry.utility';

type SortColumn = 'label' | 'mainKilos' | 'othersKilos' | 'perKiloDifference' | 'difference' | 'pctVsMedian' | 'quartile' | 'q1' | 'q2' | 'q3' | 'q4';

interface Props {
  data: {
    byShips: { [key: string]: GroupedSettlement };
    byWeeks: { [key: string]: GroupedSettlement };
  };
  comparisonType: 'byWeeks' | 'byShips';
  currencyLabel?: string;
  pctVsMedianByKey?: Record<string, number>;
  quartileDistByKey?: Record<string, QuartileDistribution>;
  comparisonTarget?: 'median' | 'q1' | 'q3';
}

export const formatXAxisLabel = (
  shipment: string,
  viewMode: 'ships' | 'weeks',
  meta?: { marketName?: string }
) => {
  if (viewMode === 'weeks') {
    // Para el formato WEEK-YYYY-WXX
    if (shipment.startsWith('WEEK-')) {
      const weekNumber = shipment.split('-W')[1];
      return `W${weekNumber}`;
    }
    return shipment;
  }

  // Para envíos marítimos y aéreos
  if (shipment.startsWith('AIR-')) {
    const weekMatch = shipment.match(/-W(\d+)/);
    const weekInfo = weekMatch ? `W${weekMatch[1]}` : '';
    const marketName = meta?.marketName;
    return marketName ? `Air ${weekInfo} ${marketName}` : `Air ${weekInfo}`;
  }

  if (shipment.startsWith('GROUND-')) {
    const [_, year, weekInfo] = shipment.split('-');
    return `Ground ${weekInfo}`;
  }

  if (shipment.startsWith('SHIP-')) {
    const shipName = shipment.split('-')[1].replace(/_/g, ' ');
    return shipName;
  }

  return shipment;
};

const ShipmentBarChart: React.FC<Props> = ({ data, comparisonType, currencyLabel, pctVsMedianByKey, quartileDistByKey, comparisonTarget = 'median' }) => {
  const { t } = useTranslation('settlementsComparative');
  const { darkMode } = useUserStore();
  const [displayMode, setDisplayMode] = useState<'value' | 'percentage'>(
    'value'
  );
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  const chartRef = useRef<HTMLDivElement>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('difference');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const isLocal = currencyLabel && currencyLabel !== 'USD';

  const fmt = (value: number, decimals = 2) =>
    isLocal
      ? `${value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} ${currencyLabel}`
      : formatMoneyUSD(value, { decimals });

  // Al inicializar el componente, asegurémonos de que las etiquetas de datos estén habilitadas
  const [dataLabelsInitialized, setDataLabelsInitialized] =
    useState<boolean>(false);

  // Después de la primera renderización, asegurémonos de que las etiquetas estén habilitadas
  useEffect(() => {
    if (!dataLabelsInitialized) {
      setDataLabelsInitialized(true);
    }
  }, []);

  const baseChartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar' as const,
        height: 350,
        foreColor: darkMode ? '#A1A1AA' : '#71717A',
        animations: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
          colors: {
            ranges: [
              { from: -Infinity, to: 0, color: '#f87171' },
              { from: 0, to: Infinity, color: '#4ade80' },
            ],
          },
        },
      },
      grid: {
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            strokeDashArray: 0,
            borderColor: '#FF0000',
            borderWidth: 2,
            opacity: 0.8,
            width: '100%',
            label: {
              borderColor: '#FF0000',
              style: { color: '#fff', background: '#FF0000' },
              text: '0',
            },
          },
        ],
      },
    }),
    []
  );

  const sortShipmentKeys = (keys: string[], currentData: { [key: string]: GroupedSettlement }) =>
    [...keys].sort((a, b) => {
      if (comparisonType === 'byWeeks') {
        return sortWeekKeys(a, b);
      }
      if (a.startsWith('AIR-') && b.startsWith('AIR-')) {
        const [_, yearA, weekA] = a.split('-');
        const [__, yearB, weekB] = b.split('-');
        return sortWeekKeys(`${yearA}-${weekA}`, `${yearB}-${weekB}`);
      }
      if (a.startsWith('SHIP-') && b.startsWith('SHIP-')) {
        const etaA = currentData[a]?.meta?.eta || '';
        const etaB = currentData[b]?.meta?.eta || '';
        if (etaA && etaB) return etaA.localeCompare(etaB);
        const keyEtaA =
          (a.match(/(\d{4}-\d{2}-\d{2}(?:T.*)?)$/) || [])[1] || '';
        const keyEtaB =
          (b.match(/(\d{4}-\d{2}-\d{2}(?:T.*)?)$/) || [])[1] || '';
        return keyEtaA.localeCompare(keyEtaB);
      }
      if (a.startsWith('AIR-')) return -1;
      if (b.startsWith('AIR-')) return 1;
      return a.localeCompare(b);
    });

  const computedData = useMemo(() => {
    const currentData = data[comparisonType];
    const shipments = sortShipmentKeys(Object.keys(currentData), currentData);

    const rows = shipments.map((shipment) => {
      const shipmentData = currentData[shipment];
      let totalDifference = 0;
      let mainKilos = 0;
      let othersKilos = 0;
      let weightedPercentageDiff = 0;
      let totalWeight = 0;

      Object.entries(shipmentData.mainClient.details).forEach(
        ([key, mainDetail]) => {
          let detailOthersKilos = 0;
          let detailOthersFob = 0;

          Object.values(shipmentData.comparisons).forEach((client) => {
            if (client.details[key]) {
              detailOthersKilos += client.details[key].kilos;
              detailOthersFob += client.details[key].fob;
            }
          });

          if (mainDetail.kilos > 0 && detailOthersKilos > 0) {
            const mainFobPerKg = mainDetail.fob / mainDetail.kilos;
            const othersFobPerKg = detailOthersFob / detailOthersKilos;
            const diffFobPerKg = mainFobPerKg - othersFobPerKg;
            const detailDifference = diffFobPerKg * mainDetail.kilos;

            const percentageDiff =
              ((mainFobPerKg - othersFobPerKg) / othersFobPerKg) * 100;
            weightedPercentageDiff += percentageDiff * mainDetail.kilos;
            totalWeight += mainDetail.kilos;

            totalDifference += detailDifference;
            mainKilos += mainDetail.kilos;
            othersKilos += detailOthersKilos;
          }
        }
      );

      const finalPercentage =
        totalWeight > 0 ? weightedPercentageDiff / totalWeight : 0;

      return {
        shipmentKey: shipment,
        label: formatXAxisLabel(
          shipment,
          comparisonType === 'byWeeks' ? 'weeks' : 'ships',
          shipmentData.meta
        ),
        difference: totalDifference,
        percentage: finalPercentage,
        mainKilos,
        othersKilos,
        perKiloDifference: mainKilos > 0 ? totalDifference / mainKilos : 0,
      };
    });

    const filtered = rows.filter(
      (entry) => entry.difference !== 0 || entry.percentage !== 0
    );

    return {
      rows: filtered,
      differenceData: filtered.map((e) => e.difference),
      percentageData: filtered.map((e) => e.percentage),
      categories: filtered.map((e) => e.label),
    };
  }, [data, comparisonType, t]);

  const totals = useMemo(() => {
    const result = computedData.rows.reduce(
      (acc, curr) => ({
        mainKilos: acc.mainKilos + curr.mainKilos,
        othersKilos: acc.othersKilos + curr.othersKilos,
        totalDifference: acc.totalDifference + curr.difference,
      }),
      { mainKilos: 0, othersKilos: 0, totalDifference: 0 }
    );

    return {
      mainKilos: result.mainKilos,
      othersKilos: result.othersKilos,
      totalDifference: result.totalDifference,
      perKiloDifference: result.mainKilos > 0 ? result.totalDifference / result.mainKilos : 0,
    };
  }, [computedData]);

  const fmtChart = (val: number) =>
    isLocal
      ? `${Math.abs(Number(val)).toLocaleString('es-CL', { maximumFractionDigits: 0 })} ${currencyLabel}`
      : `$${Math.abs(Number(val)).toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
  const fmtAxis = (val: number) =>
    isLocal
      ? `${val.toLocaleString('es-CL', { maximumFractionDigits: 0 })} ${currencyLabel}`
      : `$${val.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;

  const valueChartOptions: ApexOptions = useMemo(
    () => ({
      ...baseChartOptions,
      dataLabels: {
        enabled: true,
        formatter: (val: number) => fmtChart(val),
        style: {
          fontSize: '12px',
          colors: [darkMode ? '#fff' : '#000'],
        },
      },
      yaxis: {
        title: { text: isLocal ? `${t('Diferencia Total')} (${currencyLabel})` : t('Diferencia Total (USD)') },
        labels: {
          formatter: (val: number) => fmtAxis(val),
        },
      },
      xaxis: {
        categories: computedData.categories,
        title: { text: comparisonType === 'byWeeks' ? t('Semana') : t('Embarque') },
        labels: {
          rotate: -45,
          style: { fontSize: '11px' },
          trim: false,
          maxHeight: 180,
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => fmtAxis(val),
        },
      },
    }),
    [
      baseChartOptions,
      comparisonType,
      computedData.categories,
      dataLabelsInitialized,
      currencyLabel,
      darkMode,
    ]
  );

  const percentageChartOptions: ApexOptions = useMemo(
    () => ({
      ...baseChartOptions,
      dataLabels: {
        enabled: true,
        formatter: (val: number) => {
          const value = Number(val);
          return isNaN(value) ? '0%' : `${Math.abs(value).toFixed(2)}%`;
        },
        style: {
          fontSize: '12px',
          colors: [darkMode ? '#fff' : '#000'],
        },
      },
      yaxis: {
        title: { text: t('Diferencia (%)') },
        labels: {
          formatter: (val: number) => {
            const value = Number(val);
            return isNaN(value) ? '0%' : `${value.toFixed(2)}%`;
          },
        },
        tickAmount: 10,
        forceNiceScale: true,
      },
      xaxis: {
        categories: computedData.categories,
        title: { text: comparisonType === 'byWeeks' ? t('Semana') : t('Embarque') },
        labels: {
          rotate: -45,
          style: { fontSize: '11px' },
          trim: false,
          maxHeight: 180,
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val.toFixed(2)}%`,
        },
      },
    }),
    [
      baseChartOptions,
      comparisonType,
      computedData.categories,
      dataLabelsInitialized,
      darkMode,
    ]
  );

  return (
    <Card className='w-full md:p-4'>
      <CardHeader>
        <div className='flex flex-col sm:flex-row justify-between w-full items-center gap-4'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-bold text-center sm:text-left'>
              {t('Diferencia')}{' '}
              {comparisonType == 'byWeeks'
                ? t('por Semana')
                : comparisonType == 'byShips'
                  ? t('por Nave')
                  : t('Personalizada')}
            </h3>
            {viewMode === 'chart' && (
              <ChartDownloadButton
                chartRef={chartRef}
                title={`${t('Diferencia')} ${comparisonType == 'byWeeks' ? t('por Semana') : comparisonType == 'byShips' ? t('por Nave') : t('Personalizada')}`}
              />
            )}
          </div>
          <div className='flex gap-4 w-full sm:w-auto items-center'>
            <Tabs
              size='sm'
              selectedKey={viewMode}
              onSelectionChange={(key) =>
                setViewMode(key as 'chart' | 'table')
              }
            >
              <Tab key='chart' title={t('Gráfico')} />
              <Tab key='table' title={t('Tabla')} />
            </Tabs>
            {viewMode === 'chart' && (
              <Select
                label={t('Tipo de Valor')}
                value={displayMode}
                defaultSelectedKeys={['value']}
                onChange={(e) =>
                  setDisplayMode(e.target.value as 'value' | 'percentage')
                }
                size='sm'
                className='w-full sm:min-w-40'
              >
                <SelectItem key='value' value='value'>
                  {isLocal ? `${t('Valor')} ${currencyLabel}` : t('Valor USD')}
                </SelectItem>
                <SelectItem key='percentage' value='percentage'>
                  {t('Porcentaje')}
                </SelectItem>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody>
        {viewMode === 'chart' ? (
          <div ref={chartRef}>
            <ReactApexChart
              options={
                displayMode === 'value' ? valueChartOptions : percentageChartOptions
              }
            series={[
              {
                name:
                  displayMode === 'value'
                    ? t('Diferencia Total')
                    : t('Diferencia Porcentual'),
                data:
                  displayMode === 'value'
                    ? computedData.differenceData
                    : computedData.percentageData,
              },
            ]}
            type='bar'
            height={350}
          />
          </div>
        ) : (
          <Table removeWrapper aria-label={t('Tabla de Compensaciones')} sortDescriptor={{ column: sortColumn, direction: sortDirection === 'asc' ? 'ascending' : 'descending' }} onSortChange={(desc) => { setSortColumn(desc.column as SortColumn); setSortDirection(desc.direction === 'ascending' ? 'asc' : 'desc'); }}>
            <TableHeader>
              {[
                <TableColumn key='label' allowsSorting>
                  {comparisonType === 'byShips' ? t('Nave') : t('Semana')}
                </TableColumn>,
                <TableColumn key='mainKilos' allowsSorting>{t('Kg Principal')}</TableColumn>,
                <TableColumn key='othersKilos' allowsSorting>{t('Kg Otros')}</TableColumn>,
                <TableColumn key='difference' allowsSorting>{t('Diferencia Total')}</TableColumn>,
                <TableColumn key='perKiloDifference' allowsSorting>{t('Diferencia')} / {t('Kg Principal')}</TableColumn>,
                ...(pctVsMedianByKey ? [<TableColumn key='pctVsMedian' allowsSorting>% vs {comparisonTarget === 'median' ? 'Med.' : comparisonTarget.toUpperCase()}</TableColumn>] : []),
                ...(quartileDistByKey ? [
                  <TableColumn key='quartile' allowsSorting>KG {'>'} {comparisonTarget === 'median' ? 'Med.' : comparisonTarget.toUpperCase()}</TableColumn>,
                  <TableColumn key='q1' allowsSorting>Q1</TableColumn>,
                  <TableColumn key='q2' allowsSorting>Q2</TableColumn>,
                  <TableColumn key='q3' allowsSorting>Q3</TableColumn>,
                  <TableColumn key='q4' allowsSorting>Q4</TableColumn>,
                ] : []),
              ]}
            </TableHeader>
            <TableBody>
              {[
                <TableRow
                  key='total'
                  className='bg-default-50'
                >
                  {[
                    <TableCell key='l' className='font-bold'>{t('TOTAL')}</TableCell>,
                    <TableCell key='mk' className='font-bold'>{Math.round(totals.mainKilos).toLocaleString()}</TableCell>,
                    <TableCell key='ok' className='font-bold'>{Math.round(totals.othersKilos).toLocaleString()}</TableCell>,
                    <TableCell key='td'
                      className={`font-bold ${
                        totals.totalDifference > 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      }`}
                    >
                      {fmt(totals.totalDifference, 0)}
                    </TableCell>,
                    <TableCell key='pk'
                      className={`font-bold ${
                        totals.perKiloDifference > 0
                          ? 'text-success-500'
                          : 'text-danger-500'
                      }`}
                    >
                      {fmt(totals.perKiloDifference)}
                    </TableCell>,
                    ...(pctVsMedianByKey ? [<TableCell key='pctVsMedian' className='font-bold'>-</TableCell>] : []),
                    ...(quartileDistByKey ? [
                      <TableCell key='quartile' className='font-bold'>-</TableCell>,
                      <TableCell key='q1' className='font-bold'>-</TableCell>,
                      <TableCell key='q2' className='font-bold'>-</TableCell>,
                      <TableCell key='q3' className='font-bold'>-</TableCell>,
                      <TableCell key='q4' className='font-bold'>-</TableCell>,
                    ] : []),
                  ]}
                </TableRow>,
                ...[...computedData.rows].sort((a, b) => {
                  const getExternal = (row: typeof a): number => {
                    const qd = quartileDistByKey?.[row.shipmentKey];
                    const total = qd?.totalKilos || 1;
                    switch (sortColumn) {
                      case 'pctVsMedian': return pctVsMedianByKey?.[row.shipmentKey] ?? -Infinity;
                      case 'quartile': {
                        if (!qd) return -Infinity;
                        if (comparisonTarget === 'q1') return (qd.q2Kilos + qd.q3Kilos + qd.q4Kilos) / total;
                        if (comparisonTarget === 'q3') return qd.q4Kilos / total;
                        return (qd.q3Kilos + qd.q4Kilos) / total;
                      }
                      case 'q1': return qd ? qd.q1Kilos / total : -Infinity;
                      case 'q2': return qd ? qd.q2Kilos / total : -Infinity;
                      case 'q3': return qd ? qd.q3Kilos / total : -Infinity;
                      case 'q4': return qd ? qd.q4Kilos / total : -Infinity;
                      default: return 0;
                    }
                  };
                  if (['pctVsMedian', 'quartile', 'q1', 'q2', 'q3', 'q4'].includes(sortColumn)) {
                    const aVal = getExternal(a);
                    const bVal = getExternal(b);
                    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
                  }
                  const aVal = a[sortColumn as keyof typeof a];
                  const bVal = b[sortColumn as keyof typeof b];
                  if (typeof aVal === 'string' && typeof bVal === 'string') {
                    return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                  }
                  return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
                }).map((row) => {
                  const score = pctVsMedianByKey?.[row.shipmentKey];
                  const qd = quartileDistByKey?.[row.shipmentKey];
                  const aboveMed = qd ? Math.round((comparisonTarget === 'q1' ? (qd.q2Kilos + qd.q3Kilos + qd.q4Kilos) : comparisonTarget === 'q3' ? qd.q4Kilos : (qd.q3Kilos + qd.q4Kilos)) / qd.totalKilos * 100) : null;
                  const q1Pct = qd ? Math.round((qd.q1Kilos / qd.totalKilos) * 100) : null;
                  const q2Pct = qd ? Math.round((qd.q2Kilos / qd.totalKilos) * 100) : null;
                  const q3Pct = qd ? Math.round((qd.q3Kilos / qd.totalKilos) * 100) : null;
                  const q4Pct = qd ? Math.round((qd.q4Kilos / qd.totalKilos) * 100) : null;
                  return (
                  <TableRow key={row.shipmentKey}>
                    {[
                      <TableCell key='l'>{row.label}</TableCell>,
                      <TableCell key='mk'>{Math.round(row.mainKilos).toLocaleString()}</TableCell>,
                      <TableCell key='ok'>{Math.round(row.othersKilos).toLocaleString()}</TableCell>,
                      <TableCell key='td'
                        className={
                          row.difference > 0
                            ? 'text-success-500'
                            : 'text-danger-500'
                        }
                      >
                        {fmt(row.difference, 0)}
                      </TableCell>,
                      <TableCell key='pk'
                        className={
                          row.perKiloDifference > 0
                            ? 'text-success-500'
                            : 'text-danger-500'
                        }
                      >
                        {fmt(row.perKiloDifference)}
                      </TableCell>,
                      ...(pctVsMedianByKey ? [
                        <TableCell key='pctVsMedian' className={score != null ? (score >= 0 ? 'text-success-500' : 'text-danger-500') : ''}>
                          {score != null ? `${score >= 0 ? '+' : ''}${(score * 100).toFixed(1)}%` : '-'}
                        </TableCell>
                      ] : []),
                      ...(quartileDistByKey ? [
                        <TableCell key='quartile' className={aboveMed != null ? (aboveMed >= 50 ? 'text-success-500' : 'text-danger-500') : ''}>
                          {aboveMed != null ? `${aboveMed}%` : '-'}
                        </TableCell>,
                        <TableCell key='q1'>{q1Pct != null ? `${q1Pct}%` : '-'}</TableCell>,
                        <TableCell key='q2'>{q2Pct != null ? `${q2Pct}%` : '-'}</TableCell>,
                        <TableCell key='q3'>{q3Pct != null ? `${q3Pct}%` : '-'}</TableCell>,
                        <TableCell key='q4'>{q4Pct != null ? `${q4Pct}%` : '-'}</TableCell>,
                      ] : []),
                    ]}
                  </TableRow>
                  );
                }),
              ]}
            </TableBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

export default ShipmentBarChart;
